import { getApiUrl } from '../config/api';

export type StudentProfileFields = {
  firstName: string;
  lastName: string;
  phone: string;
  city: string;
  postalCode: string;
};

export type ProfileCompletion = {
  userId: string;
} & StudentProfileFields;

const REQUIRED_FIELDS: (keyof StudentProfileFields)[] = [
  'firstName',
  'lastName',
  'phone',
  'city',
  'postalCode',
];

function readProfileFields(infos: Record<string, unknown>): StudentProfileFields {
  return {
    firstName: String(infos.firstName ?? '').trim(),
    lastName: String(infos.lastName ?? '').trim(),
    phone: String(infos.phone ?? '').trim(),
    city: String(infos.city ?? '').trim(),
    postalCode: String(infos.postalCode ?? '').trim(),
  };
}

export function isStudentProfileComplete(
  infos: Partial<StudentProfileFields> | Record<string, unknown> | null | undefined,
): boolean {
  if (!infos) return false;

  return REQUIRED_FIELDS.every((field) => String(infos[field] ?? '').trim().length > 0);
}

export async function getStudentProfileCompletion(
  userId: string,
  role: string,
): Promise<ProfileCompletion | null> {
  if (role !== 'Student' || !userId) return null;

  const response = await fetch(getApiUrl(`/api/v1/user/${userId}`), {
    credentials: 'include',
    cache: 'no-store',
  });
  const data = await response.json().catch(() => ({}));
  const fields = readProfileFields(data?.user?.userInfos ?? {});

  return isStudentProfileComplete(fields) ? null : { userId, ...fields };
}

/** @deprecated Use getStudentProfileCompletion */
export const getLocationProfileCompletion = getStudentProfileCompletion;

export async function getAuthenticatedStudentProfileCompletion(): Promise<ProfileCompletion | null> {
  const response = await fetch(getApiUrl('/api/v1/auth/me'), {
    credentials: 'include',
    cache: 'no-store',
  });
  const data = await response.json().catch(() => ({}));

  if (!response.ok || !data?.success) {
    throw new Error('Unable to load authenticated user');
  }

  return getStudentProfileCompletion(data.data?.userId, data.data?.role);
}

/** @deprecated Use getAuthenticatedStudentProfileCompletion */
export const getAuthenticatedLocationProfileCompletion =
  getAuthenticatedStudentProfileCompletion;

export async function saveStudentProfile(
  profile: ProfileCompletion,
  fields: StudentProfileFields,
): Promise<void> {
  const response = await fetch(getApiUrl(`/api/v1/user/${profile.userId}`), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      firstName: fields.firstName.trim(),
      lastName: fields.lastName.trim(),
      phone: fields.phone.trim(),
      city: fields.city.trim(),
      postalCode: fields.postalCode.trim(),
    }),
  });
  const data = await response.json().catch(() => ({}));

  if (!response.ok || !data?.success) {
    const validationMessage = Array.isArray(data?.errors)
      ? data.errors.map((error: any) => error.msg || error.message).filter(Boolean).join(', ')
      : '';
    throw new Error(validationMessage || data?.message || 'Unable to save profile');
  }
}

/** @deprecated Use saveStudentProfile */
export async function saveLocationProfile(
  profile: ProfileCompletion,
  city: string,
  postalCode: string,
): Promise<void> {
  return saveStudentProfile(profile, {
    firstName: profile.firstName,
    lastName: profile.lastName,
    phone: profile.phone,
    city,
    postalCode,
  });
}

/** @deprecated Use ProfileCompletion */
export type LocationProfileCompletion = ProfileCompletion;
